import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Search, Plus, Check, Lock, Loader2, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { SlideOver } from '../common/SlideOver';
import { listsApi } from '../../lib/api';
import type { List, ListEntityKind } from '../../types/list';

interface AddToListSheetProps {
  open: boolean;
  onClose: () => void;
  kind: ListEntityKind;
  id: string;
  recordLabel?: ReactNode;
}

/**
 * Narrows the `unknown[]` returned by listsApi.addItems into the created
 * ListItem ids, so a this-session add can be un-toggled with removeItem.
 */
function extractCreatedItemId(items: unknown[]): string | null {
  for (const item of items) {
    if (item && typeof item === 'object' && 'id' in item) {
      const candidate = (item as { id: unknown }).id;
      if (typeof candidate === 'string') return candidate;
    }
  }
  return null;
}

/**
 * Single-record "Add to list" bottom-sheet. Opened from a school/person detail
 * header to toggle that record into/out of the user's MANUAL lists, or spin up a
 * new list inline. Smart lists are shown disabled — membership is automatic.
 *
 * MEMBERSHIP PRE-CHECK LIMITATION: there is no "which lists already contain X"
 * endpoint, so every row STARTS unchecked and only reflects THIS-session toggles.
 * We never fake membership; the created ListItem id from addItems is what lets us
 * un-toggle a list we added during this session.
 */
export function AddToListSheet({ open, onClose, kind, id, recordLabel }: AddToListSheetProps) {
  const [lists, setLists] = useState<List[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // listId -> created ListItem id (only present for lists added THIS session)
  const [addedItemIds, setAddedItemIds] = useState<Record<string, string>>({});
  // listIds confirmed already-member this session (added===0); checked but no
  // removable id — tapping again would be a no-op so we track them separately.
  const [alreadyMemberIds, setAlreadyMemberIds] = useState<Set<string>>(new Set());
  // listId currently mid-flight (add or remove) -> disable its row
  const [pendingListId, setPendingListId] = useState<string | null>(null);

  // Inline "New list…" composer
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [savingNew, setSavingNew] = useState(false);
  const newInputRef = useRef<HTMLInputElement>(null);

  const fetchLists = async () => {
    setLoading(true);
    setError(null);
    try {
      // limit caps at 100 server-side (listQuerySchema). Part 1 shows the first
      // page of the user's manual lists; pagination in the sheet is a Part-2 concern.
      const { lists: fetched } = await listsApi.list('mine', 1, 100);
      setLists(fetched);
    } catch {
      setError('Could not load your lists.');
    } finally {
      setLoading(false);
    }
  };

  // (Re)load and reset all local state whenever the sheet opens for a record.
  useEffect(() => {
    if (!open) {
      setLists([]);
      setSearch('');
      setAddedItemIds({});
      setAlreadyMemberIds(new Set());
      setPendingListId(null);
      setCreating(false);
      setNewName('');
      setSavingNew(false);
      setError(null);
      return;
    }
    void fetchLists();
    // Re-run when the target record changes so state never leaks across records.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, kind, id]);

  useEffect(() => {
    if (creating) newInputRef.current?.focus();
  }, [creating]);

  // Exclude the system Favorites list — the header star already owns favorites,
  // so showing it here as a manual row would be a confusing duplicate affordance.
  const manualLists = useMemo(
    () => lists.filter((l) => l.kind === 'MANUAL' && !l.isSystem),
    [lists],
  );
  const smartLists = useMemo(() => lists.filter((l) => l.kind === 'SMART'), [lists]);

  const term = search.trim().toLowerCase();
  const filteredManual = useMemo(
    () => (term ? manualLists.filter((l) => l.name.toLowerCase().includes(term)) : manualLists),
    [manualLists, term],
  );
  const filteredSmart = useMemo(
    () => (term ? smartLists.filter((l) => l.name.toLowerCase().includes(term)) : smartLists),
    [smartLists, term],
  );

  const toggleManual = async (list: List) => {
    if (pendingListId) return;
    const createdId = addedItemIds[list.id];
    // If the row is checked but we have no stored ListItem.id (i.e. the record
    // was confirmed already-a-member this session via added===0), we cannot call
    // removeItem — we never received a ListItem.id for it.  Treat as a no-op.
    if (alreadyMemberIds.has(list.id)) return;
    setPendingListId(list.id);
    try {
      if (createdId) {
        // Remove — only possible because we added it this session and kept the id.
        await listsApi.removeItem(list.id, createdId);
        setAddedItemIds((prev) => {
          const next = { ...prev };
          delete next[list.id];
          return next;
        });
        setLists((prev) =>
          prev.map((l) =>
            l.id === list.id ? { ...l, count: Math.max(0, (l.count ?? 0) - 1) } : l,
          ),
        );
        toast.success(`Removed from ${list.name}`);
      } else {
        const { added, items } = await listsApi.addItems(list.id, [{ kind, id }]);
        if (added > 0) {
          // Newly added — store the created id so the row can be un-toggled.
          const newId = extractCreatedItemId(items);
          if (newId) {
            setAddedItemIds((prev) => ({ ...prev, [list.id]: newId }));
          }
          setLists((prev) =>
            prev.map((l) => (l.id === list.id ? { ...l, count: (l.count ?? 0) + 1 } : l)),
          );
          toast.success(`Added to ${list.name}`);
        } else {
          // added === 0: backend deduped — record is already a member.  Mark the
          // row checked without touching the count.  We have no ListItem.id so
          // this row won't be un-toggleable this session (documented limitation).
          setAlreadyMemberIds((prev) => new Set([...prev, list.id]));
          toast(`Already in ${list.name}`);
        }
      }
    } catch {
      toast.error('Could not update list');
    } finally {
      setPendingListId(null);
    }
  };

  const handleCreate = async () => {
    const trimmed = newName.trim();
    if (!trimmed || savingNew) return;
    setSavingNew(true);
    try {
      const { list } = await listsApi.create({ name: trimmed });
      const { items } = await listsApi.addItems(list.id, [{ kind, id }]);
      const newId = extractCreatedItemId(items);
      await fetchLists();
      if (newId) {
        setAddedItemIds((prev) => ({ ...prev, [list.id]: newId }));
      }
      setCreating(false);
      setNewName('');
      toast.success(`Added to ${list.name}`);
    } catch {
      toast.error('Could not create list');
    } finally {
      setSavingNew(false);
    }
  };

  return (
    <SlideOver
      isOpen={open}
      onClose={onClose}
      title="Add to list"
      footer={
        <button type="button" onClick={onClose} className="btn-primary w-full justify-center">
          Done
        </button>
      }
    >
      <div className="-m-6">
        {/* Subtitle: which record we're adding */}
        {recordLabel && (
          <div className="px-6 pt-1 pb-4 text-sm text-gray-500 dark:text-gray-400 truncate">
            {recordLabel}
          </div>
        )}

        {/* Search */}
        <div className="px-6 pb-4">
          <div className="flex items-center gap-2 rounded-lg bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700 px-3 py-2.5">
            <Search className="h-4 w-4 flex-shrink-0 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search your lists…"
              className="w-full bg-transparent text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none"
            />
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center gap-2 px-6 py-10 text-sm text-gray-500 dark:text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading your lists…
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="px-6 py-8 text-center">
            <AlertCircle className="mx-auto mb-2 h-6 w-6 text-gray-400" />
            <p className="text-sm text-gray-600 dark:text-gray-300">{error}</p>
            <button
              type="button"
              onClick={() => void fetchLists()}
              className="btn-secondary mt-3 text-sm"
            >
              Try again
            </button>
          </div>
        )}

        {!loading && !error && (
          <div className="border-t border-gray-100 dark:border-gray-700/60">
            {/* New list… row / inline composer */}
            {creating ? (
              <div className="border-b border-gray-100 dark:border-gray-700/60 px-6 py-3">
                <div className="flex items-center gap-2">
                  <input
                    ref={newInputRef}
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void handleCreate();
                      if (e.key === 'Escape') {
                        setCreating(false);
                        setNewName('');
                      }
                    }}
                    placeholder="List name"
                    className="input text-sm"
                    disabled={savingNew}
                  />
                  <button
                    type="button"
                    onClick={() => void handleCreate()}
                    disabled={!newName.trim() || savingNew}
                    className="btn-primary flex-shrink-0 text-sm disabled:opacity-50"
                  >
                    {savingNew && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                    Create
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCreating(false);
                      setNewName('');
                    }}
                    disabled={savingNew}
                    className="flex-shrink-0 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="flex w-full items-center gap-3 border-b border-gray-100 px-6 py-3 text-left transition-colors hover:bg-gray-50 dark:border-gray-700/60 dark:hover:bg-gray-700/30"
              >
                <span className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-lg bg-lime-100 dark:bg-lime-900/30">
                  <Plus className="h-4 w-4 text-lime-700 dark:text-lime-400" strokeWidth={2.4} />
                </span>
                <span className="text-sm font-semibold text-lime-800 dark:text-lime-300">
                  New list…
                </span>
              </button>
            )}

            {/* Manual list rows */}
            {filteredManual.map((list) => {
              const checked = Boolean(addedItemIds[list.id]) || alreadyMemberIds.has(list.id);
              const isPending = pendingListId === list.id;
              return (
                <button
                  key={list.id}
                  type="button"
                  aria-pressed={checked}
                  onClick={() => void toggleManual(list)}
                  disabled={isPending}
                  className="flex w-full items-center gap-3 border-b border-gray-100 px-6 py-3 text-left transition-colors hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700/60 dark:hover:bg-gray-700/30"
                >
                  <span
                    className={`flex h-[21px] w-[21px] flex-none items-center justify-center rounded-md border ${
                      checked
                        ? 'border-[#85ba12] bg-[#85ba12]'
                        : 'border-gray-300 bg-white dark:border-gray-500 dark:bg-gray-700'
                    }`}
                  >
                    {isPending ? (
                      <Loader2
                        className={`h-3 w-3 animate-spin ${checked ? 'text-white' : 'text-gray-400'}`}
                      />
                    ) : (
                      checked && <Check className="h-3 w-3 text-white" strokeWidth={3.5} />
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {list.name}
                    </span>
                    <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                      {list.count ?? 0} items
                    </span>
                  </span>
                  <span className="flex-none rounded-full bg-lime-100 px-2 py-0.5 text-[10px] font-bold tracking-wide text-lime-800 dark:bg-lime-900/30 dark:text-lime-300">
                    MANUAL
                  </span>
                </button>
              );
            })}

            {/* Smart list rows — disabled, membership is automatic */}
            {filteredSmart.map((list) => (
              <div
                key={list.id}
                aria-disabled="true"
                className="flex w-full items-center gap-3 border-b border-gray-100 px-6 py-3 opacity-55 dark:border-gray-700/60"
              >
                <span className="flex h-[21px] w-[21px] flex-none items-center justify-center rounded-md border border-gray-200 bg-gray-100 dark:border-gray-600 dark:bg-gray-700">
                  <Lock className="h-2.5 w-2.5 text-gray-400" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {list.name}
                  </span>
                  <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                    Membership is automatic
                  </span>
                </span>
                <span className="flex-none rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold tracking-wide text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                  SMART
                </span>
              </div>
            ))}

            {/* Empty states */}
            {filteredManual.length === 0 && filteredSmart.length === 0 && (
              <div className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                {term ? 'No lists match your search.' : 'You have no lists yet — create one above.'}
              </div>
            )}
          </div>
        )}
      </div>
    </SlideOver>
  );
}
